import type { MatchState } from '../sim/state';
import type { SimConfig } from '../config/simConfig';
import type { ContactEvent } from '../physics/collide';
import type { ActionKind } from '../types';

// Foul evaluation (§7.1) — pure. For a between-opponents ContactEvent, score how foul-like it was on the
// severity ladder, then map to a discipline verdict. No I/O, no RNG (detection rolls live in detect.ts /
// refereeStep). Geometry note: behind01 uses the offender's position RELATIVE TO the victim's facing — a
// tackle from behind (offender opposite the way the victim faces) scores 1.

export type FoulVerdict = 'legal' | 'foul' | 'yellow' | 'red';

export interface Severity {
  force01: number;
  ballFirst01: number;
  behind01: number;
  actionW: number;
  severity: number;
}

const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v);
const TACKLES: ReadonlySet<ActionKind> = new Set<ActionKind>(['TACKLE_STAND', 'TACKLE_SLIDE']);

function actionWeight(action: ActionKind, cfg: SimConfig): number {
  const w = cfg.referee.actionW;
  if (action === 'TACKLE_STAND') return w.TACKLE_STAND;
  if (action === 'TACKLE_SLIDE') return w.TACKLE_SLIDE;
  if (action === 'SHOVE') return w.SHOVE;
  if (action === 'PUNCH_ACTIVE') return w.PUNCH_ACTIVE;
  return w.OTHER;
}

/**
 * Severity of `contact` treating `offender` as the aggressor and `victim` as the fouled player (§7.1).
 *   severity = W_FORCE·force01 + W_BALLFIRST·ballFirst01 + W_BEHIND·behind01 + W_ACTION·actionW
 * with a landed-punch floor (PUNCH_SEV) and a clean-ballFirst-slide ×CLEAN_SLIDE_MULT reduction.
 */
export function foulSeverity(state: MatchState, contact: ContactEvent, offenderId: number, victimId: number, cfg: SimConfig): Severity {
  const r = cfg.referee;
  const off = state.players[offenderId]!;
  const vic = state.players[victimId]!;
  // D99: score the action AS IT WAS AT CONTACT, not the live one. `applyRagdolls` (sub-step 5b) runs BEFORE
  // refereeStep (7) and rewrites `action = 'RAGDOLL'` on both participants of any impulse ≥ 10 contact — the
  // tackler included. Reading `off.action` therefore graded the very hardest challenges as OTHER-weight
  // non-tackles: actionW collapsed to w.OTHER and ballFirst01 to 0 (the ★ gate is tackle-class only), so a
  // violent slide scored BELOW a soft one and no tackle could ever reach T_RED. ContactEvent records
  // aAction/bAction for exactly this reason (physics/collide.ts) — annotateTackleContacts already relies on
  // them, and is deliberately ordered before the ragdoll for the same purpose.
  const offAction = offenderId === contact.a ? contact.aAction : contact.bAction;
  const force01 = clamp01((contact.impulse - r.F_MIN) / (r.F_MAX - r.F_MIN));
  const isTackle = TACKLES.has(offAction);
  const ballFirst01 = isTackle && contact.ballFirst !== true ? 1 : 0; // tackle-class only (★ §7.1)

  // offender position relative to the victim's facing: directly behind ⇒ behind01 = 1
  const dx = off.pos.x - vic.pos.x;
  const dy = off.pos.y - vic.pos.y;
  const len = Math.hypot(dx, dy) || 1;
  const dot = (dx / len) * Math.cos(vic.facing) + (dy / len) * Math.sin(vic.facing);
  const behind01 = clamp01(-dot);

  const actionW = actionWeight(offAction, cfg);
  let severity = r.W_FORCE * force01 + r.W_BALLFIRST * ballFirst01 + r.W_BEHIND * behind01 + r.W_ACTION * actionW;

  // clean ball-first slide with a mostly-frontal contact is barely a foul (§7.1)
  if (offAction === 'TACKLE_SLIDE' && contact.ballFirst === true && behind01 < r.CLEAN_SLIDE_BEHIND) severity *= r.CLEAN_SLIDE_MULT;
  // a landed punch is always at least a red-worthy severity
  if (offAction === 'PUNCH_ACTIVE') severity = Math.max(severity, r.PUNCH_SEV);

  return { force01, ballFirst01, behind01, actionW, severity };
}

/** Map a severity to the discipline ladder (§7.1): < T_FOUL legal, < T_YELLOW foul, < T_RED foul+yellow, else red. */
export function classifyFoul(severity: number, cfg: SimConfig): FoulVerdict {
  const r = cfg.referee;
  if (severity < r.T_FOUL) return 'legal';
  if (severity < r.T_YELLOW) return 'foul';
  if (severity < r.T_RED) return 'yellow';
  return 'red';
}
